import React, { useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { Modal, Form, Checkbox, Input, Button, Radio, Select, Flex, Spin } from "antd"
import styles from "./CustomQuestions.module.css"
import { getBookingQuestionnaire, submitQuestionnaire, updateQuestionnaireResponses } from "store/question"
import { getWaiverSubmissionsByTripBooking } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { useTranslation } from "react-i18next"
import { toaster } from "components/common/Toast"
import { InfoCircleOutlined, WarningOutlined } from "@ant-design/icons"
const CustomQuestions = ({ questionnaireEditable, uuid, bookingQuestionnaireId, visible = true, onClose, onSubmit, hideEditWarning = false }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { questionaire_data } = useSelector((state) => state.question)
  const questions = useMemo(() => questionaire_data?.data?.questions || [], [questionaire_data?.data?.questions])
  const hasExistingAnswers = questions.some((q) => q.answers && q.answers.length > 0)
  const isReadOnly = !questionnaireEditable && hasExistingAnswers
  const loading = questionaire_data?.loading
  const { loading: isLoading } = useSelector((state) => state.question)
  useEffect(() => {
    if (bookingQuestionnaireId) {
      dispatch(getBookingQuestionnaire(bookingQuestionnaireId))
    }
  }, [bookingQuestionnaireId, dispatch])

  const [form] = Form.useForm()


  // Set form initial values when answers are available
  useEffect(() => {
    if (questions.length > 0 && hasExistingAnswers) {
      const initialValues = {}
      questions.forEach((question) => {
        if (question.answers && question.answers.length > 0) {
          const fieldName = `question_${question.id}`
          question.answers.forEach((answer) => {
            if (answer.questions_option_id) {
              if (question.question_type === "checkbox") {
                if (!initialValues[fieldName]) initialValues[fieldName] = []
                initialValues[fieldName].push(answer.questions_option_id)
              } else {
                initialValues[fieldName] = answer.questions_option_id
              }
            } else if (answer.answer_text) {
              initialValues[fieldName] = answer.answer_text
            }
          })
        }
      })
      form.setFieldsValue(initialValues)
    }
  }, [questions, form, hasExistingAnswers])

  const formatAnswer = (question, value) => {
    if (!value) {
      return {
        question_id: question.id,
        answer_text: "",
        questions_option_id: null
      }
    }

    if (question.question_type === "paragraph") {
      return {
        question_id: question.id,
        answer_text: value,
        questions_option_id: null
      }
    }

    // For radio, dropdown - values are now option IDs, but always send as array
    if (question.question_type === "radio" || question.question_type === "dropdown") {
      const selectedId = Array.isArray(value) ? value[0] : value
      return {
        question_id: question.id,
        answer_text: "",
        questions_option_id: selectedId ? [Number(selectedId)] : null
      }
    }

    // For checkbox - values are already option IDs
    if (question.question_type === "checkbox") {
      const selectedIds = Array.isArray(value) ? value : [value]
      return {
        question_id: question.id,
        answer_text: "",
        questions_option_id: selectedIds.length > 0 ? selectedIds : null
      }
    }
  }

  const handleFinish = async (values) => {
    try {
      // Validate all required fields
      const requiredFields = questions.filter((q) => q.required)
      const missingFields = []
      requiredFields.forEach((q) => {
        const fieldName = `question_${q.id}`
        if (!values[fieldName] || (Array.isArray(values[fieldName]) && values[fieldName].length === 0)) {
          missingFields.push(fieldName)
        }
      })

      if (missingFields.length > 0) {
        const firstError = document.querySelector(`[name="${missingFields[0]}"]`)
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" })
        }
        return
      }

      // Check if there are existing answers to determine if we should update or submit
      // console.log(hasExistingAnswers,'hasExistingAnswers')
      console.log("Has existing answers:", hasExistingAnswers)
      console.log("Questions with answers:", questions.filter((q) => q.answers && q.answers.length > 0).length)

      // Format the answers according to the required payload
      // Skip non-required questions that have no value filled in
      const formattedAnswers = questions
        .filter((question) => {
          if (question.required) return true
          const fieldName = `question_${question.id}`
          const value = values[fieldName]
          if (!value) return false
          if (Array.isArray(value) && value.length === 0) return false
          if (typeof value === "string" && !value.trim()) return false
          return true
        })
        .map((question) => {
          const fieldName = `question_${question.id}`
          return formatAnswer(question, values[fieldName])
        })

      if (onSubmit) {
        await onSubmit({ answers: formattedAnswers })
      }

      if (hasExistingAnswers) {
        // Update existing answers
        const updatePayload = {
          answers: []
        }

        questions.forEach((question) => {
          const fieldName = `question_${question.id}`
          const currentValue = values[fieldName]
          const existingAnswers = question.answers || []

          // Handle different question types
          if (question.question_type === "checkbox") {
            const selectedIds = Array.isArray(currentValue) ? currentValue : []

            if (existingAnswers.length > 0) {
              // Mark existing answers for deletion if they're not in current selection
              existingAnswers.forEach((existingAnswer) => {
                const isStillSelected = selectedIds.includes(existingAnswer.questions_option_id)
                updatePayload.answers.push({
                  id: existingAnswer.id,
                  questions_option_id: existingAnswer.questions_option_id,
                  _destroy: !isStillSelected
                })
              })
            }

            // Add new selections
            selectedIds.forEach((optionId) => {
              const alreadyExists = existingAnswers.some((ans) => ans.questions_option_id === optionId)
              if (!alreadyExists) {
                updatePayload.answers.push({
                  question_id: question.id,
                  questions_option_id: optionId,
                  _destroy: false
                })
              }
            })
          } else {
            // For radio, dropdown, and paragraph questions
            const existingAnswer = existingAnswers[0]
            if (existingAnswer) {
              // Update existing answer
              const answerData = {
                id: existingAnswer.id,
                _destroy: false
              }

              if (question.question_type === "paragraph") {
                answerData.answer_text = currentValue || ""
              } else {
                answerData.questions_option_id =
                  currentValue && currentValue.questions_option_id ? currentValue.questions_option_id : currentValue
              }

              updatePayload.answers.push(answerData)
            } else if (currentValue) {
              // Add new answer (for questionnaireEditable with no existing answers)
              const answerData = {
                question_id: question.id,
                _destroy: false
              }

              if (question.question_type === "paragraph") {
                answerData.answer_text = currentValue
              } else {
                answerData.questions_option_id = currentValue
              }

              updatePayload.answers.push(answerData)
            } else if (questionnaireEditable) {
              // For questionnaireEditable, include empty answer to ensure update is triggered
              updatePayload.answers.push({
                question_id: question.id,
                answer_text: "",
                questions_option_id: null,
                _destroy: false
              })
            }
          }
        })

        console.log("Update payload:", updatePayload)
        console.log("Booking questionnaire ID:", bookingQuestionnaireId)

        // Always call updateQuestionnaireResponses when questionnaireEditable is true
        if (questionnaireEditable) {
          // For questionnaireEditable, always try to update
          const updateRes = await dispatch(updateQuestionnaireResponses(bookingQuestionnaireId, updatePayload))
          console.log("Update response:", updateRes)
          if (updateRes.message) {
            toaster.success(updateRes.message)
          }
        } else {
          // For non-editable with existing answers, just submit
          const submitRes = await dispatch(submitQuestionnaire(bookingQuestionnaireId, formattedAnswers))
          if (submitRes.message) {
            toaster.success(submitRes.message)
          }
        }

        dispatch(getWaiverSubmissionsByTripBooking(uuid))
      } else if (questionnaireEditable) {
        // First time submission with questionnaireEditable - only submit, don't update
        const res = await dispatch(submitQuestionnaire(bookingQuestionnaireId, formattedAnswers))
        if (res.message) {
          toaster.success(res.message)
        }
        
        dispatch(getWaiverSubmissionsByTripBooking(uuid))
      } else {
        // First time submission without questionnaireEditable
        const res = await dispatch(submitQuestionnaire(bookingQuestionnaireId, formattedAnswers))
        if (res.message) {
          toaster.success(res.message)
          dispatch(getWaiverSubmissionsByTripBooking(uuid))
        }
      }

      if (onClose) onClose()
    } catch (error) {
      console.error("Error submitting form:", error)
    }
  }

  const renderQuestionInput = (question) => {
    const commonProps = {
      key: question.id,
      label: question.question_text + (question.required ? "*" : ""),
      name: `question_${question.id}`,
      rules: question.required
        ? [
            {
              validator: (_, value) => {
                if (question.required && (!value || (Array.isArray(value) && value.length === 0))) {
                  return Promise.reject(new Error(t("settings.question_required")))
                }
                return Promise.resolve()
              }
            }
          ]
        : [],
      className: styles.formItem
    }

    switch (question.question_type) {
      case "checkbox":
        return (
          <Form.Item {...commonProps}>
            <Checkbox.Group className={styles.checkboxGroup} disabled={isReadOnly}>
              {question.questions_options.map((opt) => (
                <Checkbox key={opt.id} value={opt.id} className={styles.checkbox}>
                  {opt.option_text}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
        )

      case "radio":
        return (
          <Form.Item {...commonProps}>
            <Radio.Group className={styles.radioGroup} disabled={isReadOnly}>
              {question.questions_options.map((opt) => (
                <Radio key={opt.id} value={opt.id} className={styles.radio}>
                  {opt.option_text}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        )

      case "dropdown":
        return (
          <Form.Item {...commonProps}>
            <Select className={styles.select} placeholder={t("settings.select_placeholder")} disabled={isReadOnly}>
              {question.questions_options.map((opt) => (
                <Select.Option key={opt.id} value={opt.id}>
                  {opt.option_text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )

      case "paragraph":
        return (
          <Form.Item {...commonProps}>
            <Input.TextArea rows={4} className={styles.textarea} placeholder={t("settings.paragraph_placeholder")} disabled={isReadOnly} />
          </Form.Item>
        )

      default:
        return null
    }
  }

  return (
    <Modal
      maskClosable={false}
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      className={styles.modal}
      width={800}
      destroyOnClose
    >
      <div className={styles.header}>{t("settings.quesionnaire")}</div>
      <FirstLoading new={!loading}>
        <Spin spinning={loading}>
          {questionnaireEditable && hasExistingAnswers && (
            <div className={styles.infoAlert}>
              <span className={styles.infoIcon}>
                <InfoCircleOutlined />
              </span>
              <span>{t("digital_waiver_and_contracts.changes_can_not_be_made")}</span>
            </div>
          )}
          {!questionnaireEditable && hasExistingAnswers && !hideEditWarning && (
            <div className={styles.infoWarning}>
              <span className={styles.infoWarningIcon}>
                <WarningOutlined />
              </span>
              <span>{t("digital_waiver_and_contracts.can_not_edit")}</span>
            </div>
          )}

          <Form form={form} layout="vertical" onFinish={handleFinish} className={styles.form}>
            <div className={styles.questionCard}>{questions.map(renderQuestionInput)}</div>
            <Flex gap={6} className={styles.buttonGroup}>
              <Button onClick={onClose} color="primary" variant="outlined">
                {isReadOnly ? t("global.close") : t("global.cancel")}
              </Button>
              {!(!questionnaireEditable && hasExistingAnswers) && (
                <Button htmlType="submit" color="primary" variant="solid" loading={isLoading} disabled={isLoading}>
                  {hasExistingAnswers ? "Save" : "Send form"}
                </Button>
              )}
            </Flex>
            {/* </Flex> */}
          </Form>
        </Spin>
      </FirstLoading>
    </Modal>
  )
}

CustomQuestions.propTypes = {
  questionnaireEditable: PropTypes.bool,
  uuid: PropTypes.string,
  bookingQuestionnaireId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func
}

CustomQuestions.defaultProps = {
  questionnaireEditable: false,
  uuid: "",
  visible: true,
  bookingQuestionnaireId: "",
  onSubmit: () => {}
}

export default CustomQuestions
